import { useEffect, useMemo, useState } from 'react';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { PhotoThumb } from '@/components/PhotoThumb';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { adoptionApi } from '../api';
import type { ApplicationStatus, ApplicationWithDetails, AdoptionApplicationTimeline } from '../types';
import { APPLICATION_STATUS_LABELS } from '../types';
import { Timeline } from '../components/Timeline';
import { CalendarDays, FileText, PawPrint, UploadCloud, ArrowUpDown, ArrowUp, ArrowDown, ExternalLink } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLookupsSnapshot, toOptionLabel } from '@/features/lookups/snapshot';
import { Input } from '@/components/ui/input';

type Mode = 'shelter' | 'admin';

type Row = ApplicationWithDetails & {
  adoption?: any;
};

type SortKey = 'id' | 'animal' | 'shelter' | 'adopter' | 'createdAt' | 'status';

const STATUS_COLORS: Record<ApplicationStatus, string> = {
  SUBMITTED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-100',
  IN_REVIEW: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-100',
  APPROVED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-100',
  REJECTED: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-100',
  CANCELLED: 'bg-slate-200 text-slate-800 dark:bg-slate-900/40 dark:text-slate-100',
};

function ApplicationsManagementPage({ mode }: { mode: Mode }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const shelterId = mode === 'shelter' ? (user as any)?.shelterId || (user as any)?.shelter?.id : undefined;
  const { data: lookups } = useLookupsSnapshot();

  const [applications, setApplications] = useState<Row[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState<Row | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [formResponse, setFormResponse] = useState<any | null>(null);
  const [formTemplate, setFormTemplate] = useState<any | null>(null);
  const [timeline, setTimeline] = useState<AdoptionApplicationTimeline[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | ApplicationStatus>('ALL');
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const animalPhotoUrl = (animal?: any) => {
    const raw = animal?.primaryPhotoUrl || animal?.photo;
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    return `${import.meta.env.VITE_API_URL}${raw}`;
  };

  const absoluteFileUrl = (raw?: string | null) => {
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    return `${String(import.meta.env.VITE_API_URL || '').replace(/\/$/, '')}/${raw.replace(/^\//, '')}`;
  };

  const contractUrlFor = (app?: Row | null) => {
    if (!app) return '';
    return (app as any).adoption?.contractUrl || (app as any).contractUrl || '';
  };

  const animalSexLabel = (animal?: any) => {
    if (!animal) return undefined;
    if (animal.sex_label) return animal.sex_label;
    const id = animal.sexId || animal.sex_id;
    if (!id || !lookups?.sexes) return undefined;
    const found = lookups.sexes.find((s: any) => Number(s.id) === Number(id));
    return found ? toOptionLabel(found) : undefined;
  };

  const adopterDisplayName = (app: Row) => {
    const profile = (app as any).adopterProfile || (app as any).adopter_profile || (app as any).adopter;
    const user = profile?.user || (app as any).adopterUser || (app as any).user;
    const fullName =
      user?.fullName ||
      user?.full_name ||
      user?.name ||
      user?.nome ||
      profile?.fullName ||
      profile?.full_name ||
      profile?.name ||
      profile?.nome ||
      '';
    const firstLast = [
      profile?.firstName,
      profile?.lastName,
      profile?.first_name,
      profile?.last_name,
    ]
      .filter(Boolean)
      .join(' ')
      .trim();
    return fullName || firstLast || user?.email || profile?.email || '';
  };

  const formatDate = (value?: string | number | Date) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
  };

  const statusOptions = useMemo(
    () => Object.keys(APPLICATION_STATUS_LABELS) as ApplicationStatus[],
    []
  );

  const loadApplications = async () => {
    setIsLoading(true);
    try {
      const filter = {
        order: ['createdAt DESC'],
        include: [
          { relation: 'animal', scope: { fields: ['id', 'name', 'statusId', 'photo', 'primaryPhotoUrl', 'sex_label', 'sexId', 'sex_id', 'city', 'department', 'microchip'] } },
          { relation: 'shelter', scope: { fields: ['id', 'name'] } },
          {
            relation: 'adopterProfile',
            scope: {
              fields: ['id', 'firstName', 'lastName', 'first_name', 'last_name', 'fullName', 'full_name', 'name', 'email', 'phone'],
              include: [{ relation: 'user', scope: { fields: ['id', 'fullName', 'email', 'phone'] } }],
            },
          },
        ],
      };
      const data = await adoptionApi.listApplications({
        shelterId: shelterId || undefined,
        filter,
      });
      setApplications(data as Row[]);
    } catch {
      setApplications([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shelterId, mode]);

  const filteredApplications = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return applications.filter((app) => {
      if (statusFilter !== 'ALL' && app.status !== statusFilter) return false;
      if (!q) return true;
      const haystack = [
        `#${app.id}`,
        app.animal?.name || '',
        adopterDisplayName(app),
        app.shelter?.name || '',
        APPLICATION_STATUS_LABELS[app.status] || app.status,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [applications, searchTerm, statusFilter]);

  const getSortValue = (app: Row, key: SortKey) => {
    switch (key) {
      case 'id':
        return Number(app.id) || 0;
      case 'animal':
        return (app.animal?.name || '').toLowerCase();
      case 'shelter':
        return (app.shelter?.name || '').toLowerCase();
      case 'adopter':
        return adopterDisplayName(app).toLowerCase();
      case 'status':
        return (APPLICATION_STATUS_LABELS[app.status] || app.status).toLowerCase();
      case 'createdAt':
      default:
        return app.createdAt ? new Date(app.createdAt).getTime() : 0;
    }
  };

  const sortedApplications = useMemo(() => {
    const list = [...filteredApplications];
    list.sort((a, b) => {
      const aValue = getSortValue(a, sortKey);
      const bValue = getSortValue(b, sortKey);
      let result = 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        result = aValue - bValue;
      } else {
        result = String(aValue).localeCompare(String(bValue));
      }
      return sortDir === 'asc' ? result : -result;
    });
    return list;
  }, [filteredApplications, sortKey, sortDir]);

  const openDetail = async (app: Row) => {
    setSelected(app);
    setIsDetailOpen(true);
    setIsDetailLoading(true);
    try {
      const [full, tl] = await Promise.all([
        adoptionApi.getApplicationById(app.id),
        adoptionApi.getApplicationTimeline(app.id).catch(() => []),
      ]);
      const merged = { ...app, ...(full as any) } as Row;
      setSelected(merged);
      setTimeline(Array.isArray(tl) ? tl : []);
      const response =
        (full as any).formResponse ||
        (full as any).adoptionFormResponse ||
        (full as any).form_response ||
        null;
      setFormResponse(response);
      setFormTemplate(
        (full as any).adoptionFormTemplate ||
        (full as any).formTemplate ||
        null
      );
      setNotes((merged as any).notes || '');
    } catch {
      setTimeline([]);
      setFormResponse(null);
      setFormTemplate(null);
    } finally {
      setIsDetailLoading(false);
    }
  };

  const renderAnswers = () => {
    const answers = formResponse?.answersJson || formResponse?.answers;
    if (!answers) {
      return <div className="text-sm text-muted-foreground">No hay respuestas disponibles.</div>;
    }

    const templateSections: any[] = formTemplate?.schemaJson?.sections || [];

    const lookupLabelFor = (field: any, raw: any) => {
      if (raw === undefined || raw === null || raw === '') return undefined;

      if (typeof raw === 'object') {
        const lbl = (raw as any).label || (raw as any).name || (raw as any).city || (raw as any).title;
        if (lbl) return String(lbl);
      }

      // Options defined in template
      const option = (field.options || []).find((opt: any) => String(opt.value) === String(raw));
      if (option) return option.label || option.value;

      const numeric = Number(raw);

      const findIn = (collection?: any[]) =>
        collection?.find((i: any) => Number(i.id) === numeric || Number(i.value) === numeric || String(i.code) === String(raw));

      const fromLookupType = () => {
        switch (field.lookupType) {
          case 'species': return findIn(lookups?.species);
          case 'sex': return findIn(lookups?.sexes);
          case 'size': return findIn(lookups?.sizes);
          case 'breed': return findIn(lookups?.breeds);
          case 'color': return findIn(lookups?.colors);
          case 'status': return findIn(lookups?.statuses);
          default: return undefined;
        }
      };

      const fromCommonIds = () => {
        const idMatchers: Record<string, any[]> = {
          preferred_species_id: lookups?.species,
          species_id: lookups?.species,
          speciesId: lookups?.species,
          preferred_size_id: lookups?.sizes,
          size_id: lookups?.sizes,
          sizeId: lookups?.sizes,
          preferred_sex_id: lookups?.sexes,
          sex_id: lookups?.sexes,
          sexId: lookups?.sexes,
          preferred_breed_id: lookups?.breeds,
          breed_id: lookups?.breeds,
          breedId: lookups?.breeds,
          preferred_color_id: lookups?.colors,
          color_id: lookups?.colors,
          colorId: lookups?.colors,
          city: lookups?.cities,
          city_id: lookups?.cities,
          cityId: lookups?.cities,
        };
        const collection = idMatchers[field.id] || idMatchers[field.binding?.column || ''];
        return findIn(collection);
      };

      const found = fromLookupType() || fromCommonIds();
      return found ? toOptionLabel(found as any) : undefined;
    };

    const renderValue = (value: any, field?: any) => {
      if (Array.isArray(value)) return value.map((v) => renderValue(v, field)).join(', ');
      if (typeof value === 'object' && value !== null) {
        const objLabel =
          (value as any).label ||
          (value as any).name ||
          (value as any).city ||
          (value as any).title;
        if (objLabel) return String(objLabel);
      }
      if (value === true || value === 'true') return 'Sí';
      if (value === false || value === 'false') return 'No';
      if (value === undefined || value === null || value === '') return '—';
      if (field) {
        const label = lookupLabelFor(field, value);
        if (label) return label;
      }
      return String(value);
    };

    if (templateSections.length) {
      return (
        <div className="space-y-4">
          {templateSections.map((section) => (
                <div key={section.id} className="border rounded-lg p-3">
                  <h5 className="font-semibold mb-2 text-sm">{section.label || section.title || section.id}</h5>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {(section.fields || []).map((field: any) => (
                      <div key={field.id} className="text-sm">
                        <p className="text-muted-foreground">{field.label || field.title || field.id}</p>
                        <p className="font-medium">{renderValue(answers[field.id], field)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
        </div>
      );
    }

    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {Object.entries(answers).map(([key, value]) => (
          <div key={key} className="text-sm">
            <p className="text-muted-foreground">{key}</p>
            <p className="font-medium">{renderValue(value)}</p>
          </div>
        ))}
      </div>
    );
  };

  const reloadSelected = async () => {
    if (selected) {
      await openDetail(selected);
    }
  };

  const handleSaveNotes = async () => {
    if (!selected) return;
    setIsActionLoading(true);
    try {
      await adoptionApi.updateApplication(selected.id, { notes });
      toast({ title: 'Notas guardadas' });
      await reloadSelected();
    } catch {
      toast({ title: 'No se pudieron guardar las notas', variant: 'destructive' });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleUploadContract = async () => {
    if (!selected || !contractFile) return;
    setIsActionLoading(true);
    try {
      await adoptionApi.uploadApplicationContract(selected.id, contractFile);
      toast({ title: 'Contrato subido' });
      setContractFile(null);
      await reloadSelected();
    } catch {
      toast({ title: 'No se pudo subir el contrato', variant: 'destructive' });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: ApplicationStatus) => {
    if (!selected) return;
    if (selected.status === 'APPROVED' || selected.status === 'CANCELLED') {
      return;
    }
    setStatusUpdating(true);
    try {
      await adoptionApi.updateApplicationStatus(selected.id, { status: newStatus });
      toast({ title: `Estado actualizado a ${APPLICATION_STATUS_LABELS[newStatus] || newStatus}` });
      await loadApplications();
      await reloadSelected();
    } catch {
      toast({ title: 'No se pudo actualizar el estado', variant: 'destructive' });
    } finally {
      setStatusUpdating(false);
    }
  };

  const isReadOnly = mode === 'admin';
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const renderSortIcon = (key: SortKey) => {
    if (sortKey !== key) return <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />;
    return sortDir === 'asc' ? (
      <ArrowUp className="inline h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="inline h-3 w-3 ml-1" />
    );
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-6xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {mode === 'admin' ? 'Solicitudes de adopción (Global)' : 'Solicitudes de Adopción'}
            </h1>
            <p className="text-muted-foreground text-sm">
              {mode === 'admin'
                ? 'Vista sólo lectura de todas las solicitudes.'
                : 'Gestiona y actualiza las solicitudes vinculadas a tu refugio.'}
            </p>
          </div>
        </div>

        <Card className="p-4 space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-3 md:flex-row md:flex-1">
              <Input
                placeholder="Buscar por ID, animal, adoptante o refugio..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:max-w-sm"
              />
              <Select
                value={statusFilter}
                onValueChange={(value) => setStatusFilter(value === 'ALL' ? 'ALL' : (value as ApplicationStatus))}
              >
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="Todos los estados" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos los estados</SelectItem>
                  {statusOptions.map((status) => (
                    <SelectItem key={status} value={status}>
                      {APPLICATION_STATUS_LABELS[status] || status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-muted-foreground">
              Mostrando {sortedApplications.length} de {applications.length} solicitudes
            </div>
          </div>
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground text-sm">Cargando solicitudes...</div>
          ) : sortedApplications.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">No hay solicitudes registradas.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead onClick={() => handleSort('id')} className="cursor-pointer select-none">
                    ID {renderSortIcon('id')}
                  </TableHead>
                  <TableHead onClick={() => handleSort('animal')} className="cursor-pointer select-none">
                    Animal {renderSortIcon('animal')}
                  </TableHead>
                  <TableHead onClick={() => handleSort('shelter')} className="cursor-pointer select-none">
                    Entidad {renderSortIcon('shelter')}
                  </TableHead>
                  <TableHead onClick={() => handleSort('adopter')} className="cursor-pointer select-none">
                    Adoptante {renderSortIcon('adopter')}
                  </TableHead>
                  <TableHead onClick={() => handleSort('createdAt')} className="cursor-pointer select-none">
                    Fecha {renderSortIcon('createdAt')}
                  </TableHead>
                  <TableHead onClick={() => handleSort('status')} className="cursor-pointer select-none">
                    Estado {renderSortIcon('status')}
                  </TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedApplications.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell className="font-medium">#{app.id}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {animalPhotoUrl(app.animal) ? (
                          <PhotoThumb
                            src={animalPhotoUrl(app.animal)!}
                            alt={app.animal.name || 'Animal'}
                            className="w-10 h-10 rounded"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                            <PawPrint className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <div className="font-semibold">{app.animal?.name || `Animal #${app.animalId}`}</div>
                          <div className="text-xs text-muted-foreground">{app.animal?.species_label || ''}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{app.shelter?.name || '—'}</TableCell>
                    <TableCell>
                      <div className="text-sm">{adopterDisplayName(app) || '—'}</div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <CalendarDays className="h-4 w-4" />
                        {formatDate(app.createdAt)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[app.status]}>
                        {APPLICATION_STATUS_LABELS[app.status] || app.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm" onClick={() => openDetail(app)}>
                        Ver detalle
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Solicitud #{selected?.id}</DialogTitle>
          </DialogHeader>
          {isDetailLoading ? (
            <div className="py-6 text-muted-foreground text-sm">Cargando detalles...</div>
          ) : !selected ? (
            <div className="py-6 text-muted-foreground text-sm">Selecciona una solicitud para ver sus datos.</div>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={STATUS_COLORS[selected.status]}>
                    {APPLICATION_STATUS_LABELS[selected.status] || selected.status}
                  </Badge>
                  {selected.shelter?.name && (
                    <span className="text-sm text-muted-foreground">Refugio: {selected.shelter.name}</span>
                  )}
                </div>
                {selected.animal && (
                  <div className="flex items-center gap-3">
                    <a
                      href={`/adopt?animalId=${selected.animal.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3"
                    >
                      {animalPhotoUrl(selected.animal) ? (
                        <PhotoThumb
                          src={animalPhotoUrl(selected.animal)!}
                          alt={selected.animal.name || 'Animal'}
                          className="w-16 h-16 rounded-lg"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                          <PawPrint className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="space-y-1">
                        <p className="font-semibold hover:underline">
                          {selected.animal.name || `Animal #${selected.animal.id}`}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {selected.animal.species_label || ''} {selected.animal.size_label ? `· ${selected.animal.size_label}` : ''}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {animalSexLabel(selected.animal) || 'Sexo no especificado'}
                        </p>
                        {(selected.animal.city || selected.animal.department) && (
                          <p className="text-sm text-muted-foreground">
                            {selected.animal.city || ''}{selected.animal.city && selected.animal.department ? ' - ' : ''}{selected.animal.department || ''}
                          </p>
                        )}
                        {selected.animal.microchip && (
                          <p className="text-xs text-muted-foreground">Microchip: {selected.animal.microchip}</p>
                        )}
                      </div>
                    </a>
                  </div>
                )}

              <Card className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <h4 className="font-semibold">Adoptante</h4>
                </div>
                <div className="text-sm text-muted-foreground">
                  {(selected as any).adopterProfile?.user?.fullName ||
                    (selected as any).adopterProfile?.user?.full_name ||
                    '—'}
                </div>
                {(selected as any).adopterProfile?.user?.email && (
                  <div className="text-sm text-muted-foreground">
                    {(selected as any).adopterProfile?.user?.email}
                  </div>
                )}
                {(selected as any).adopterProfile?.user?.phone && (
                  <a
                    className="text-sm text-muted-foreground hover:underline"
                    href={`https://wa.me/${String((selected as any).adopterProfile?.user?.phone).replace(/\\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Tel/WhatsApp: {(selected as any).adopterProfile?.user?.phone}
                  </a>
                )}
              </Card>
              </div>

              <Card className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <h4 className="font-semibold">Formulario y respuestas</h4>
                </div>
                {renderAnswers()}
              </Card>

              <Card className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <h4 className="font-semibold">Notas internas</h4>
                </div>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Seguimiento interno, llamadas, visitas..."
                  disabled={isReadOnly || isActionLoading}
                />
                {!isReadOnly && (
                  <div>
                    <Button size="sm" onClick={handleSaveNotes} disabled={isActionLoading}>
                      Guardar notas
                    </Button>
                  </div>
                )}
              </Card>

              <Card className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <h4 className="font-semibold">Historial de estados</h4>
                </div>
                <Timeline events={timeline} />
              </Card>

              {!isReadOnly && (
                <Card className="p-4 space-y-3">
                  <div className="space-y-2">
                    <label className="text-sm text-muted-foreground">Estado</label>
                    <Select
                      defaultValue={selected.status}
                      onValueChange={(value) => handleStatusChange(value as ApplicationStatus)}
                      disabled={statusUpdating}
                    >
                      <SelectTrigger className="w-56">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SUBMITTED">Enviada</SelectItem>
                        <SelectItem value="IN_REVIEW">En revisión</SelectItem>
                        <SelectItem value="APPROVED">Aprobada</SelectItem>
                        <SelectItem value="REJECTED">Rechazada</SelectItem>
                        <SelectItem value="CANCELLED">Cancelada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {contractUrlFor(selected) ? (
                    <Button size="sm" variant="secondary" asChild>
                      <a href={absoluteFileUrl(contractUrlFor(selected))} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" /> Ver contrato
                      </a>
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <label className="text-sm text-muted-foreground flex items-center gap-2">
                        <UploadCloud className="h-4 w-4" /> Subir contrato firmado
                      </label>
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={(e) => setContractFile(e.target.files?.[0] || null)}
                        disabled={isActionLoading}
                      />
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={handleUploadContract}
                        disabled={isActionLoading || !contractFile}
                      >
                        Enviar contrato
                      </Button>
                    </div>
                  )}
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export function ShelterApplicationsPage() {
  return <ApplicationsManagementPage mode="shelter" />;
}

export function AdminApplicationsPage() {
  return <ApplicationsManagementPage mode="admin" />;
}

export default ApplicationsManagementPage;
